import { Components, Theme } from '@mui/material/styles';
import type {} from '@mui/x-data-grid-premium/themeAugmentation';
import type {} from '@mui/x-tree-view/themeAugmentation';

declare module '@mui/material/Button' {
	interface ButtonPropsVariantOverrides {
		loginButton: true;
		meinBereich: true;
		antragAnlegen: true;
		einstellungen: true;
		secondaryButton: true;
	}
}

const createComponents = (theme: Theme): Components => ({
	/**
	 * Typography
	 */
	MuiTypography: {
		styleOverrides: {
			root: {
				wordWrap: 'break-word',
			},
		},
	},
	/**
	 * Container
	 */
	MuiContainer: {
		defaultProps: {
			component: 'section',
		},
		styleOverrides: {
			root: {
				marginTop: '2rem',
				[theme.breakpoints.up('lg')]: {
					width: '75rem',
				},
			},
		},
	},
	MuiGrid2: {
		styleOverrides: {
			root: {
				'@media print': {
					padding: 0,
				},
				'.form-container': {
					backgroundColor: '#fff',
					marginTop: '0.5rem',
					marginLeft: 'unset',
					marginRight: 'unset',
				},
			},
		},
	},
	/**
	 * App Bar (Header-Navigation Menu)
	 */
	MuiAppBar: {
		styleOverrides: {
			root: {
				background: theme.palette.common.white,
				boxShadow: 'none',
			},
		},
	},
	/**
	 * Mui paper
	 */
	MuiPaper: {
		styleOverrides: {
			root: {
				boxShadow: 'none',
				borderRadius: 0,
			},
		},
	},
	/**
	 * Mui badge
	 */
	MuiBadge: {
		styleOverrides: {
			root: {
				// '& .MuiBadge-badge': {
				// 	transform: `scale(0.9) translate(0%, 50%)`,
				// 	backgroundColor: '#fff',
				// },
			},
		},
	},
	/**
	 * Snackbar
	 */
	MuiSnackbar: {
		styleOverrides: {
			root: {
				'@media (min-width: 0px)': {
					pointerEvents: 'auto',
					top: '9rem',
					left: '0',
					right: 0,
					width: '100%',
					maxWidth: '75rem',
					margin: 'auto',
					padding: '0 1.875rem',
					boxShadow: 'unset',
					'& .MuiAlert-root': {
						width: 'auto',
						boxShadow: '0px 3px 12px #42788767',
					},
				},
			},
		},
	},
	/**
	 * Tooltip
	 */
	MuiTooltip: {
		defaultProps: {
			arrow: true,
		},
		styleOverrides: {
			tooltip: {
				backgroundColor: '#ffffff',
				color: '#757575',
				fontSize: '1rem',
				lineHeight: '1.5',
				boxShadow: '5px 5px 25px #0000001A',
				padding: '1rem',
			},
			arrow: {
				color: '#ffffff',
			},
		},
	},
	/**
	 * Bread Crumb
	 */
	MuiBreadcrumbs: {
		styleOverrides: {
			root: {
				'& .MuiBreadcrumbs-separator': {
					color: theme.palette.secondary.main,
				},
			},
		},
	},
	/**
	 * Accordion
	 */
	MuiAccordion: {
		styleOverrides: {
			root: {
				margin: '0.5rem 0',
			},
		},
	},
	MuiAccordionDetails: {
		styleOverrides: {
			root: {
				backgroundColor: theme.palette.common.white,
			},
		},
	},
	/**
	 * Button
	 */
	MuiButton: {
		defaultProps: {
			variant: 'contained',
		},
		variants: [
			{
				props: { variant: 'loginButton' },
				style: {
					width: '100%',
					backgroundColor: theme.palette.primary.main,
					border: `1px solid ${theme.palette.primary.main}`,
					color: '#fff',
					'&:hover': {
						bgColor: theme.palette.background.paper,
						border: `1px solid ${theme.palette.primary.main}`,
						color: theme.palette.primary.main,
					},
				},
			},
			{
				props: { variant: 'meinBereich' },
				style: {
					width: '100%',
					backgroundColor: 'transparent',
					border: `1px solid ${theme.palette.primary.main}`,
					borderRadius: 0,
					color: theme.palette.primary.main,
					padding: '0.75rem 1.375rem', // 12px 22px
					fontSize: '0.9rem',
					maxWidth: '100%',
					'&.Mui-disabled': {
						color: theme.palette.text.disabled,
						borderColor: theme.palette.text.disabled,
					},
				},
			},
			{
				props: { variant: 'antragAnlegen' },
				style: {
					width: '100%',
					backgroundColor: theme.palette.primary.main,
					border: `1px solid ${theme.palette.primary.main}`,
					borderRadius: 0,
					color: theme.palette.primary.contrastText,
					padding: '0.75rem 1.375rem', // 12px 22px
					fontSize: '0.9rem',
					maxWidth: '100%',
					'&.Mui-disabled': {
						color: theme.palette.text.disabled,
						borderColor: theme.palette.text.disabled,
					},
					'&:hover': {
						bgColor: theme.palette.primary.light,
						border: `1px solid ${theme.palette.primary.main}`,
						color: '#9A1006',
					},
				},
			},
			{
				props: { variant: 'einstellungen' },
				style: {
					width: '100%',
					backgroundColor: theme.palette.secondary.main, //backgroundColor: '#0B0B60',
					border: `1px solid ${theme.palette.secondary.main}`, //border: `1px solid #0B0B60`,
					borderRadius: 0,
					color: theme.palette.primary.contrastText,
					padding: '0.75rem 1.375rem', // 12px 22px
					fontSize: '0.9rem',
					maxWidth: '100%',
					'&.Mui-disabled': {
						color: theme.palette.text.disabled,
						borderColor: theme.palette.text.disabled,
					},
					'&:hover': {
						bgColor: theme.palette.primary.light,
						border: `1px solid ${theme.palette.secondary.main}`,
						color: '#000',
					},
				},
			},
			{
				props: { variant: 'secondaryButton' },
				style: {
					backgroundColor: theme.palette.secondary.main,
					border: `1px solid ${theme.palette.secondary.main}`,
					color: '#fff',
					boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
					'&.Mui-disabled': {
						backgroundColor: theme.palette.text.disabled,
						color: '#fff',
						borderColor: theme.palette.text.disabled,
						boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
					},
					'&:hover': {
						bgColor: theme.palette.secondary.main,
						border: `1px solid ${theme.palette.secondary.main}`,
						color: '#fff',
					},
				},
			},
		],
		styleOverrides: {
			root: {
				borderRadius: 0,
				'@media print': {
					display: 'none',
				},
				textTransform: 'none',
				maxWidth: '16rem',
				'&.Mui-disabled': {
					color: theme.palette.text.disabled,
				},
			},
		},
	},
	/**
	 * Card
	 */
	MuiCardActions: {
		styleOverrides: {
			root: { justifyContent: 'flex-end', padding: '1rem 1rem 2rem 1rem' },
		},
	},
	/**
	 * Checkbox
	 */
	MuiFormControlLabel: {
		styleOverrides: {
			root: {
				'& .MuiFormControlLabel-label.Mui-disabled': {
					color: theme.palette.primary.main,
				},
			},
		},
	},
	/**
	 * TextField
	 */
	MuiTextField: {
		styleOverrides: {
			root: {
				marginTop: '20px',
				marginRight: '15px',
			},
		},
	},
	MuiInputBase: {
		styleOverrides: {
			root: {
				backgroundColor: '#fff', // this change the background color of each TextField
			},
		},
	},
	MuiInputLabel: {
		defaultProps: {
			shrink: true,
		},
		styleOverrides: {
			root: {
				padding: '0',
				whiteSpace: 'normal',
				position: 'static',
				transform: 'none',
				'&.Mui-disabled': {
					color: '#000',
				},
			},
		},
	},
	/**
	 * hides the span element where default label from MuiTextfield was shown
	 */
	MuiOutlinedInput: {
		styleOverrides: {
			root: {
				'@media print': {
					fieldset: {
						borderWidth: '0 0 1px 0 !important',
						borderStyle: 'solid !important',
						borderColor: '#000 !important',
						borderRadius: '0 !important',
					},
				},
				'legend span': {
					display: 'none',
				},
				'&::placeholder': {
					color: '#0ff',
				},
				'&.Mui-disabled': {
					backgroundColor: theme.palette.action.disabled,
					color: theme.palette.text.disabled,
					'& input': {
						'-webkit-text-fill-color': theme.palette.text.disabled,
					},
					'& textarea': {
						'-webkit-text-fill-color': theme.palette.text.disabled,
					},
					'& svg': {
						display: 'none',
					},
				},
				'& .MuiSelect-select.Mui-disabled': {
					backgroundColor: theme.palette.action.disabled,
					color: theme.palette.text.disabled,
					'-webkit-text-fill-color': theme.palette.text.disabled,
				},
				fieldset: {
					borderWidth: '1px',
					borderColor: theme.palette.border.primary,
				},
			},
		},
	},
	/**
	 * Mui Tabs
	 */
	MuiTabs: {
		styleOverrides: {
			root: {
				'& .MuiTabs-indicator': {
					left: 0,
					width: '9px',
					borderTopLeftRadius: '80px 80px',
					borderBottomLeftRadius: '80px 80px',
				},
			},
		},
	},
	MuiTab: {
		styleOverrides: {
			root: {
				alignItems: 'center',
				justifyContent: 'flex-start',
				textTransform: 'none',
				fontSize: '1rem',
				borderTopLeftRadius: '9px',
				borderBottomLeftRadius: '9px',
				minHeight: '3rem',
			},
		},
	},
	/**
	 * MUI Dialog
	 */
	MuiDialogActions: {
		styleOverrides: {
			root: {
				justifyContent: 'space-between',
				// display: 'unset',
				padding: '1rem',
			},
		},
	},

	/**
	 * MUI TABLE
	 */
	MuiTableContainer: {
		styleOverrides: {
			root: {
				border: '1px solid #EAF0F1',
			},
		},
	},
	MuiTableCell: {
		styleOverrides: {
			root: {
				'& .MuiFormControl-root': { margin: '0 !important' },
			},
		},
	},
	/**
	 * MUI DataGrid
	 */
	MuiDataGrid: {
		styleOverrides: {
			root: {
				backgroundColor: '#fff',
				'& .message-read-status--false--message-folder-Posteingang': {
					fontFamily: 'inherit',
					fontWeight: '700',
				},
				'--DataGrid-rowBorderColor': theme.palette.border.primary,
				'&.MuiDataGrid-filterFormColumnInput': { marginTop: 'unset' },
				'& .MuiDataGrid-row': { '--rowBorderColor': theme.palette.border.primary },
				'& .MuiDataGrid-withBorderColor': { borderColor: theme.palette.border.primary },
				'& .MuiDataGrid-row--borderBottom': { borderBottomColor: theme.palette.border.primary },
				'& .MuiDataGrid-columnHeader': { backgroundColor: '#fff' },
				'& .MuiDataGrid-columnHeaderTitle': { fontWeight: 500 },
				'& .MuiDataGrid-toolbarContainer': {
					padding: '8px 4px',
					borderBottomColor: theme.palette.border.primary,
					borderBottomWidth: '1px',
					borderBottomStyle: 'solid',
				},
				'& .MuiButtonBase-root': { backgroundColor: '#fff', color: theme.palette.text.primary, boxShadow: 'none' },
				'& .MuiButtonBase-root:hover': {
					backgroundColor: '#fff',
					color: theme.palette.text.primary,
					boxShadow: 'none',
				},
				'& .MuiDataGrid-cell': { cursor: 'pointer' },
				'& .MuiDataGrid-cell--pinnedRight': { backgroundColor: 'transparent !important' },
			},
		},
	},
	/**
	 * MUI MuiRichTreeView
	 */
	MuiTreeItem2: {
		styleOverrides: {
			root: {
				'&:before': {
					content: '""',
					position: 'absolute',
					left: '12px',
					top: '-4px',
					bottom: 0,
					width: '2px',
					backgroundColor: theme.palette.primary.main,
				},
				'&:first-of-type:before': {
					top: '10px',
				},
				'&:last-child:before': {
					bottom: '10px',
				},
			},
		},
	},
});

export default createComponents;
